import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import Clickable from 'components/Common/Clickable'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import CheckboxButton from 'LuxKit/Checkbox/CheckboxButton'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { buildCruiseShipRoomCapacities } from 'lib/offer/occupancyUtils'
import { MAX_CRUISE_CABIN_LIMIT } from 'constants/cruise'
import CruiseOccupancyPicker from './CruiseOccupancyPicker'

export interface CruiseOfferTravellerSelectionModalResult {
  occupancies: Array<App.Occupants>
  interestInConnectedCabins: boolean
}

interface Props {
  offer: App.CruiseOffer
  departure: App.CruiseDepartureDetails
  initialOccupancies: Array<App.Occupants>
}

function CruiseOfferTravellerSelectionModal(props: Props) {
  const { offer, departure, initialOccupancies } = props
  const intl = useIntl()
  const modalElement = useModalElementContext<CruiseOfferTravellerSelectionModalResult | undefined>()
  const shipOccupancies = offer.ship.occupancies
  const maxInfantAge = shipOccupancies?.infantMaximumAge || 2
  const departureDate = departure.departureDate ?? undefined

  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>(initialOccupancies)
  const [interestInConnectedCabins, setInterestInConnectedCabins] = useState(false)

  const capacities = useMemo(
    () => buildCruiseShipRoomCapacities(shipOccupancies),
    [shipOccupancies],
  )

  const onToggleConnectCabins = useCallback(() => {
    setInterestInConnectedCabins(v => !v)
  }, [])

  const onApply = useCallback(() => {
    modalElement.resolve({
      occupancies,
      interestInConnectedCabins,
    })
  }, [modalElement, occupancies, interestInConnectedCabins])

  const onDismiss = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  return (
    <Modal
      size="L"
      flush
      flushVerticalTop
      title={intl.formatMessage({
        id: 'S6Irt6R7F6vv5eFVQupy',
        defaultMessage: 'Who’s going?',
        description: 'Heading for traveler selection step in cruise checkout',
      })}
      primaryActionText={intl.formatMessage({
        id: 'aEUuUkfSGMLHfeFHJgZX',
        defaultMessage: 'Continue',
        description: 'Continue button text in cruise traveler selection step',
      })}
      onPrimaryActionClick={onApply}
      secondaryActionText={intl.formatMessage({
        id: 'wuFwjoMBKsrrwtvUELet',
        defaultMessage: 'Cancel',
        description: 'Button to cancel occupancy selection for insurance',
      })}
      onSecondaryActionClick={onDismiss}
      onClose={onDismiss}
    >
      <Sheet
        background="normal-background"
        borderRadius="M"
        padding="20 24"
        tabletPadding="24 32"
      >
        <Group direction="vertical" gap={20}>
          {!!capacities[0].total && (
            <BodyText variant="medium" colour="light-foreground-alt">
              {intl.formatMessage(
                {
                  id: 'BxXdq7V0gG-RuuGV0eUZ',
                  defaultMessage: 'Maximum capacity per cabin is {maxCapacity} passengers',
                  description: 'Maximum cabin capacity information in cruise traveler selection',
                },
                { maxCapacity: capacities[0].total },
              )}
            </BodyText>
          )}
          <CruiseOccupancyPicker
            departureId={departure.id}
            maxRooms={MAX_CRUISE_CABIN_LIMIT}
            saleUnit="cabin"
            capacities={capacities}
            occupancies={occupancies}
            childrenAgeLabel=""
            maxChildAge={shipOccupancies?.childMaximumAge || 17}
            maxInfantAge={maxInfantAge}
            minInfantAge={shipOccupancies?.infantMinimumAge || 0}
            onChange={setOccupancies}
            departureDate={departureDate}
            childVariant="date-of-birth"
            enableMultiRoom
          />

          {occupancies.length > 1 && (
            <Clickable onClick={onToggleConnectCabins}>
              <BodyTextBlock
                variant="medium"
                startIcon={<CheckboxButton size="large" checked={interestInConnectedCabins} data-testid="cruise-offer-connect-cabins-checkbox" />}
              >
                {intl.formatMessage({
                  id: '9tPYmdU-khOBWfSGTNqT',
                  defaultMessage: 'I’m interested in connecting cabins',
                  description: 'Checkbox label for interest in connecting cabins in cruise checkout',
                })}
                <BodyText variant="medium" colour="light-foreground-alt">
                  {intl.formatMessage({
                    id: 'yKYURLwt2UZbrCm971Kj',
                    defaultMessage: "Please note that connecting cabins cannot be guaranteed. If available, you can choose the location of your cabins in the 'Location' step.",
                    description: 'Disclaimer about connecting cabins availability in cruise checkout',
                  })}
                </BodyText>
              </BodyTextBlock>
            </Clickable>
          )}
        </Group>
      </Sheet>
    </Modal>
  )
}

export default CruiseOfferTravellerSelectionModal
